import { getUserAccount } from './user';
import API from '../../utils/api';
import * as types from './types';
import {
    AGENT_SERVICE_ENDPOINT_URL,
    BET_PLACEMENT_SUBMIT_BUTTON_TYPES,
    device_URI,
    ERROR_CODES,
    MINIMUM_ODD_VALUE,
    PRODUCER_TYPES,
    TICKET_TYPES,
    TICKET_TYPES_ENUM,
} from '../../constants/defaultValues';
import { getTranslation } from '../../utils/translation';
import { acceptOddChangeEnum, producerTypeEnum } from '../../constants/enums';
import { isEmpty } from '../../utils/merger';
import { formatSpecifierName, formatTeamNames, showErrorNotification } from '../../utils/render';
import {
    BET_SLIP_MAX_STAKE_LOADING,
    BET_SLIP_SUBMIT_BUTTON_CHANGED,
    UPDATE_BET_SLIP_BET_LINES,
} from './types';
import AgentService from '../../services/AgentService';
import { localeMoment } from '../../utils/moment';
import { store } from '../index';
import betSlipCalculator from '../../utils/betSlipCalculator';

export const changeBetSlipActiveTab = tab => ({
    type: types.CHANGE_BET_SLIP_ACTIVE_TAB,
    payload: tab,
});

export const changeBetSlipOddDecision = decision => ({
    type: types.CHANGE_BET_SLIP_ODD_CHANGE_DECISION,
    payload: decision,
});

export const addBetSlipBetLine = betLine => (dispatch, getStore) => {
    const reduxStore = getStore();

    if (
        betLine.closed ||
        (!isEmpty(betLine.marketStatus) && betLine.marketStatus !== 0) ||
        (!isEmpty(betLine.outcomeStatus) && betLine.outcomeStatus !== 0) ||
        betLine.odd < MINIMUM_ODD_VALUE ||
        reduxStore.betSlip.isTicketSubmitting
    ) {
        console.error(`Failed to select outcome`, {
            betLine,
        });
        return;
    }

    dispatch({
        type: types.ADD_BET_SLIP_BET_LINE,
        payload: betLine,
    });
};

export const addTicketBetSlip = ticketCode => async (dispatch, getState) => {
    try {
        const { info, item: ticket } = await AgentService.addTicketBetSlip(ticketCode);

        if (!info.success) return;

        const reduxStore = getState();

        const { betLines, systemValues } = ticket;

        const ticketType = TICKET_TYPES_ENUM[ticket.ticketType];

        const locale = reduxStore.settings.locale;
        const stakes = reduxStore.betSlip.stakes;

        const newBetLines = betLines.map(({ event, stake, banco }) => {
            const playerName = event.playerNames?.[event.market.outcome.outcomeRefId];

            const formatName = (name, event, locale) => {
                return formatSpecifierName({
                    name: name,
                    specifiers: event.market.specifiers,
                    teams: formatTeamNames(event.teams, locale),
                    tournamentName: event.tournamentName,
                    playerNames: event.playerNames,
                });
            };
            const formattedMarketName = formatName(event.market.name, event, locale);
            const formattedMarketTemplateName = formatName(
                event.market.templateName,
                event,
                locale,
            );
            const formattedOutcomeName = formatName(
                event.market.outcome.name || playerName,
                event,
                locale,
            );
            const formattedOutcomeTemplateName = formatName(
                event.market.outcome.templateName || playerName,
                event,
                locale,
            );

            return {
                eventId: event.eventId,
                programCode: event.programCode,
                mbn: event.market.mbn,
                teams: event.teams,
                odd: event.market.outcome.odd,
                currentOdd: event.market.outcome.odd,
                changeType: '',
                marketId: event.market.marketId,
                outcomeId: event.market.outcome.outcomeRefId,
                marketRefId: event.market.marketRefId,
                marketStatus: event.market.status,
                outcomeName: formattedOutcomeName,
                outcomeStatus: event.market.outcome.status,
                marketName: formattedMarketName,
                marketTemplateName: formattedMarketTemplateName,
                outcomeTemplateName: formattedOutcomeTemplateName,
                marketLabel: event.market.marketLabel,
                marketHash: event.market.marketHash,
                stake: stake,
                closed: event.market.status === 1 || event.market.frozen,
                isLive: event.live,
                producerType: event.live ? producerTypeEnum.LIVE : producerTypeEnum.PRE_MATCH,
                banco: banco,
                marketVersion: 'string',
                frozen: event.market.frozen,
                sportTypeId: event.sportTypeRefId,
                tournamentId: event.tournamentId,
                specifiers: event.market.specifiers,
            };
        });

        const systemCombinations = systemValues
            .split(' ')
            .map(v => v.trim())
            .filter(v => v)
            .map(v => v.split('/')[0])
            .map(v => Number(v));

        let stake = stakes[ticketType];

        switch (ticket.ticketType) {
            case TICKET_TYPES.combo: {
                stake = ticket.stake;
                break;
            }
            case TICKET_TYPES.system: {
                stake = ticket.stakePerBet;
                break;
            }
            default: {
            }
        }

        dispatch(clearBetSlip());
        dispatch({
            type: types.ADD_TICKET_BET_SLIP,
            payload: {
                betLines: newBetLines,
                activeTab: ticketType,
                stakes: { ...stakes, [ticketType]: stake },
                systemCombinations,
            },
        });

        return info;
    } catch (err) {
        const info = err.response?.data?.info;

        if (!info?.success) {
            showErrorNotification(info.errors[0].reason || 'Something went wrong');
            return info;
        }
    }
};

export const addCouponToBetSlip = couponDetails => async dispatch => {
    try {
        dispatch(clearBetSlip());
        dispatch(setBetSlipEventsLoading(true));

        const response = await API.post(`${AGENT_SERVICE_ENDPOINT_URL}/bet-slip`, {
            markets: couponDetails.bets.map(betLine => ({
                marketId: betLine.marketId,
                outcomeId: betLine.outcomeId,
            })),
        });

        const { info, item } = response.data;

        if (!info.success) {
            showErrorNotification(getTranslation('generalErrorModalTitle'));
            return;
        }

        const { betSlipItems } = item;

        const { acceptanceType: oddChangeDecision } = couponDetails;
        let hasOddsChanged = false;

        const betLines = couponDetails.bets.map(betLine => {
            const id = `${betLine.marketId}${betLine.outcomeId}`;
            const foundBetLine = betSlipItems[id];

            if (foundBetLine) {
                switch (oddChangeDecision) {
                    case acceptOddChangeEnum.NO: {
                        if (foundBetLine.odd !== betLine.odd) hasOddsChanged = true;
                        break;
                    }
                    case acceptOddChangeEnum.HIGHER: {
                        if (foundBetLine.odd < betLine.odd) hasOddsChanged = true;
                        break;
                    }
                    default: {
                        hasOddsChanged = false;
                    }
                }

                return {
                    ...betLine,
                    ...foundBetLine,
                    isLive: foundBetLine.live,
                    currentOdd: foundBetLine.odd,
                };
            } else {
                return {
                    ...betLine,
                    closed: true,
                };
            }
        });

        dispatch({
            type: types.BET_SLIP_COUPON_CHANGED,
            payload: {
                betLines,
                activeTab: couponDetails.ticketType,
                selectedOddChangeDecision: couponDetails.acceptanceType,
                selectedCombinations: couponDetails.selectedCombinations,
                stakes: couponDetails.stakes,
                couponBarcode: couponDetails.couponBarcode,
                isMobile: couponDetails.isMobile,
                hasOddsChanged,
            },
        });
    } catch (error) {
        console.error(error);
        showErrorNotification(getTranslation('generalErrorModalTitle'));
        dispatch(setBetSlipEventsLoading(false));
    }
};

export const deleteBetSlipBetLine = betLine => (dispatch, getState) => {
    if (!getState().betSlip.isTicketSubmitting) {
        dispatch({
            type: types.DELETE_BET_SLIP_BET_LINE,
            payload: betLine,
        });
    }
};

export const changeBetLineStake = betLine => ({
    type: types.CHANGE_BET_SLIP_BET_LINE_STAKE,
    payload: betLine,
});

export const changeBetSlipStakes = ({ type, stake }) => ({
    type: types.BET_SLIP_STAKES_CHANGED,
    payload: { type, stake },
});

export const changeBetSlipCombinations = combinations => ({
    type: types.BET_SLIP_COMBINATIONS_CHANGED,
    payload: combinations,
});

export const fetchBetSlipEvents =
    ({ setLoading = true } = {}) =>
    async (dispatch, getState) => {
        const betLines = getState().betSlip.betLines;

        if (betLines.length === 0) return;

        if (setLoading) {
            dispatch(setBetSlipEventsLoading(true));
        }

        await API.post(`${AGENT_SERVICE_ENDPOINT_URL}/bet-slip`, {
            markets: betLines.map(betLine => ({
                marketId: betLine.marketId,
                outcomeId: betLine.outcomeId,
            })),
        })
            .then(async res => {
                const { info, item } = res.data;
                if (!info.success) return;
                const { betSlipItems } = item;
                dispatch({
                    type: types.BET_SLIP_EVENTS_LOADED,
                    payload: betSlipItems,
                });
            })
            .catch(() => dispatch(setBetSlipEventsLoading(false)));
    };

export const clearBetSlip = () => ({
    type: types.CLEAR_BET_SLIP,
});

export const printTicket = async ticket => {
    try {
        const response = await API.post(`${device_URI}/api/print/ticket`, ticket);

        return response;
    } catch (error) {
        console.error('Printing ticket failed');
    }
};

export const printVoucher = async voucher => {
    try {
        const locale = store.getState().settings.locale;

        const response = await API.post(`${device_URI}/api/print/voucher`, {
            ...voucher,
            createdAt: localeMoment(locale)(voucher.createdAt).format('DD MMMM YYYY HH:mm:ss'),
            expiredAt: localeMoment(locale)(voucher.expiredAt).format('DD MMMM YYYY HH:mm:ss'),
            cancellationTime: voucher.cancellationTime
                ? localeMoment(locale)(voucher.cancellationTime).format('DD MMMM YYYY HH:mm:ss')
                : null,
            reprintTime: voucher.isReprint
                ? localeMoment(locale)().format('DD MMMM YYYY HH:mm:ss')
                : null,
        });

        return response;
    } catch (error) {
        console.error('Printing voucher failed');
    }
};

export const setBetBanco = betLine => ({
    type: types.SET_BET_BANCO,
    payload: betLine,
});

export const startPlaceBetRequest = () => ({
    type: types.PLACE_BET_REQUEST,
});

export const setRequestId = payload => ({
    type: types.BET_SLIP_REQUEST_ID_CHANGED,
    payload,
});

export const placeBet = ticketDetails => async (dispatch, getState) => {
    dispatch(startPlaceBetRequest());

    const requestId = '000' + (Date.now() * Math.random() * 10000000).toString().slice(0, 13);

    const { isMobile, couponBarcode } = getState().betSlip.couponData;

    const body = {
        ...ticketDetails,
        printableResponse: false,
        isMobile,
        bookBetCode: couponBarcode,
    };

    try {
        await API.post(`${AGENT_SERVICE_ENDPOINT_URL}/tickets`, body, {
            headers: {
                'x-request-id': requestId,
            },
        });

        dispatch(
            setRequestId({
                requestId,
                createDate: new Date().getTime(),
            }),
        );
    } catch (error) {
        const apiError = error.response?.data?.info?.errors?.[0];

        if (apiError !== undefined) {
            dispatch(setPlaceBetErrors([apiError]));
        }
    }
};

export const getBetDetails = requestId => async (dispatch, getStore) => {
    try {
        const res = await AgentService.getTicketByRequestId({ requestId });

        const { item } = res;

        dispatch(setRequestId(null));
        dispatch(setPlacedTicket(item));

        const { couponBarcode } = getStore().betSlip.couponData;

        await printTicket({ ...item, couponBarcode });
    } catch (error) {
        const apiError = error.response?.data?.info?.errors?.[0];

        if (apiError !== undefined) {
            dispatch(setRequestId(null));

            if (apiError.code === ERROR_CODES.oddsChanged) {
                dispatch(changeBetSlipSubmitButtonType(BET_PLACEMENT_SUBMIT_BUTTON_TYPES.confirm));
            } else if (apiError.code === ERROR_CODES.mtsTicketCreationInProgress) {
                setTimeout(() => {
                    dispatch(getBetDetails(requestId));
                }, 2000);
            } else {
                dispatch(setPlaceBetErrors([apiError]));
            }
        } else {
            setTimeout(() => {
                dispatch(getBetDetails(requestId));
            }, 2000);
        }
    }
};

export const setPlacedTicket = ticketData => ({
    type: types.BET_SLIP_PLACED_TICKET_CHANGED,
    payload: ticketData,
});

export const setPlaceBetErrors = errors => ({
    type: types.PLACE_BET_FAILED,
    payload: errors,
});

export const clearPlaceBetErrors = () => ({
    type: types.CLEAR_BET_SLIP_PLACE_ERRORS,
});

export const setBetSlipEventsLoading = isLoading => ({
    type: types.BET_SLIP_EVENTS_LOADING,
    payload: isLoading,
});

export const changeBetSlipSubmitButtonType = buttonType => ({
    type: BET_SLIP_SUBMIT_BUTTON_CHANGED,
    payload: buttonType,
});

export const updateBetLines = betLines => ({
    type: UPDATE_BET_SLIP_BET_LINES,
    payload: betLines,
});

export const setBetSLipMaxStake = state => ({
    type: BET_SLIP_MAX_STAKE_LOADING,
    payload: state,
});

export const getMaxStake = betLine => async (dispatch, getState) => {
    dispatch(setBetSLipMaxStake(true));

    const betSlipState = getState().betSlip;

    const {
        betLines: allBetLines,
        activeTab,
        stakes,
        selectedCombinations,
        selectedOddChangeDecision,
        couponData,
    } = betSlipState;
    const { isMobile, couponBarcode } = couponData;

    const betLines = betLine ? [betLine] : allBetLines;

    const { totalOdds, totalStake, expectedBetsCount, potentialPayout } =
        betSlipCalculator.calculateTicketDetails({
            betLines,
            activeTab,
            stakes,
            selectedCombinations,
        });

    const ticketData = {
        totalStake: totalStake,
        totalOdds: totalOdds,
        expectedBets: expectedBetsCount,
        ticketType: activeTab,
        acceptanceType: selectedOddChangeDecision,
        maxPaymentAmount: potentialPayout,
        bets: betLines.map(betLine => ({
            ...betLine,
            marketName: betLine.marketTemplateName,
            marketLabel: betLine.marketLabel,
            producerType: betLine.isLive ? PRODUCER_TYPES.live : PRODUCER_TYPES.preMatch,
        })),
        systemValues: selectedCombinations.map(
            system => system + betSlipCalculator.getBankers(betLines).length,
        ),
        printableResponse: false,
        isMobile,
        bookBetCode: couponBarcode,
    };

    try {
        const response = await AgentService.getMaxStake(ticketData);

        return response.item;
    } catch (error) {
        console.error('Could not get max stake response', error);

        return null;
    } finally {
        dispatch(setBetSLipMaxStake(false));
    }
};
